import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { fetchCompanies, sendBatchOnboarding } from "../api/adminClient";
import { parseSpreadsheetFile } from "../utils/parseSpreadsheetFile";
import DuplicateEmailConfirmDialog from "./DuplicateEmailConfirmDialog";
import {
  assessmentFormatRequiredError,
  INVITE_ROLES,
  SAMPLE_CSV,
} from "./inviteConstants";
import InviteRoleField from "./InviteRoleField";

let uidCounter = 0;
function nextUid() {
  uidCounter += 1;
  return `${Date.now()}-${uidCounter}`;
}

function normalizeRole(raw, roleOptions) {
  const value = String(raw || "").trim();
  if (!value) return "";
  const roles = roleOptions?.length ? roleOptions : INVITE_ROLES;
  const exact = roles.find((r) => r.toLowerCase() === value.toLowerCase());
  // Free-text designations are allowed — keep the typed value when unmatched.
  return exact || value;
}

function Spinner({ className = "h-5 w-5" }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

/**
 * v1-style bulk invite: CSV/XLSX upload, preview table, batch create.
 */
export default function BulkInviteModal({
  open,
  onClose,
  companyName: initialCompanyName = "",
  lockCompany = false,
  onSuccess,
  roleOptions,
  sampleCsv,
}) {
  const roles = roleOptions?.length ? roleOptions : INVITE_ROLES;
  const csvSample = sampleCsv || SAMPLE_CSV;
  const [companies, setCompanies] = useState([]);
  const [bulkCompany, setBulkCompany] = useState("");
  const [extractedRows, setExtractedRows] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [duplicateConfirm, setDuplicateConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBulkCompany(initialCompanyName || "");
    setExtractedRows([]);
    setPreviewFile(null);
    setDuplicateConfirm(null);
    setBusy(false);
    fetchCompanies()
      .then((res) => setCompanies(res.data?.companies || []))
      .catch(() => {});
  }, [open, initialCompanyName]);

  const companyName = (lockCompany ? initialCompanyName : bulkCompany).trim();

  const invalidRows = useMemo(
    () =>
      extractedRows.filter((r) => !r.email?.trim() || !r.designation?.trim()),
    [extractedRows]
  );

  const canInvite =
    !!companyName && extractedRows.length > 0 && invalidRows.length === 0;

  if (!open) return null;

  const parseBulkFile = async (file) => {
    setDuplicateConfirm(null);
    setBusy(false);
    try {
      const json = await parseSpreadsheetFile(file);
      const cols = Object.keys(json[0] || {}).map((c) => c.trim().toLowerCase());
      const required = ["first name", "last name", "email", "role"];
      if (!required.every((c) => cols.includes(c))) {
        toast.error(
          "Invalid file structure. Ensure columns: First Name, Last Name, Email, Role."
        );
        setExtractedRows([]);
        return;
      }
      const rows = json.map((item) => {
        const rawRole = String(item.Role || item.role || "").trim();
        return {
          _uid: nextUid(),
          first_name: item["First Name"] || item["first name"] || "",
          last_name: item["Last Name"] || item["last name"] || "",
          email: String(item.Email || item.email || "").trim(),
          designation: normalizeRole(rawRole, roles),
          rawRole,
        };
      });
      setExtractedRows(rows);
      const missingRole = rows.filter((r) => !r.designation).length;
      if (missingRole) {
        toast.error(
          `${missingRole} row(s) need a valid role — choose from the dropdown in the table.`
        );
      } else {
        toast.success("Data extracted successfully!");
      }
    } catch {
      toast.error("Error reading file");
      setExtractedRows([]);
    }
  };

  const submitBulkInvite = async (forceProceed = false) => {
    if (!companyName) {
      toast.error(lockCompany ? "Select a company in step 1 first" : "Select a company");
      return;
    }
    if (!extractedRows.length) {
      toast.error("Please upload a valid CSV or Excel file before inviting users");
      return;
    }
    if (invalidRows.length) {
      toast.error("Please ensure every row has email and a valid role");
      return;
    }

    const formatError = assessmentFormatRequiredError(companies, { companyName });
    if (formatError) {
      toast.error(formatError);
      return;
    }

    setBusy(true);
    try {
      const res = await sendBatchOnboarding({
        company_name: companyName,
        participants: extractedRows.map((r) => ({
          email: r.email.trim(),
          first_name: String(r.first_name || "").trim(),
          last_name: String(r.last_name || "").trim(),
          designation: r.designation,
        })),
        pre_survey: "N",
        is_proceed: forceProceed ? "Y" : "N",
        send_email: false,
        frontend_origin: window.location.origin,
      });

      if (res.data?.needs_confirmation) {
        setBusy(false);
        setDuplicateConfirm({
          email: res.data.email || "",
          otherCompanies: res.data.other_companies || [],
          username: res.data.username || "",
          confirmationReason: res.data.confirmation_reason || "other_company",
        });
        return;
      }

      const created = res.data?.invites || [];
      toast.success(`Invited ${created.length} participant(s)`);
      setDuplicateConfirm(null);
      onClose();
      onSuccess?.({ invites: created, raw: res.data });
    } catch (e) {
      toast.error(e.message || "Bulk invite failed");
    } finally {
      setBusy(false);
    }
  };

  const modal = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white w-full max-w-xl p-6 rounded-lg shadow-lg overflow-auto relative"
        style={{ maxHeight: "32rem" }}
      >
        <button
          type="button"
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl leading-none"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <h2 className="text-center font-semibold text-xl mb-4 text-[#004f73] pr-8">
          Bulk Invite
        </h2>

        {!lockCompany ? (
          <div className="mb-3">
            <label className="block text-sm mb-1 font-medium text-gray-700">
              Company <span className="text-red-600">*</span>
            </label>
            <select
              value={bulkCompany}
              onChange={(e) => setBulkCompany(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-gray-50"
            >
              <option value="">Select company</option>
              {companies.map((c) => (
                <option key={c.id} value={c.company_name}>
                  {c.company_name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p className="text-sm text-gray-600 mb-3">
            Company: <strong className="text-[#004f73]">{initialCompanyName}</strong>
          </p>
        )}

        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 border-gray-300">
          {previewFile ? (
            <p className="text-sm text-green-700">{previewFile.name}</p>
          ) : (
            <p className="text-sm text-gray-600 px-4 text-center">
              <span className="font-medium">Click to upload</span> CSV or Excel (.xlsx)
            </p>
          )}
          <input
            type="file"
            className="hidden"
            accept=".xlsx,.csv"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setPreviewFile({ name: f.name });
              parseBulkFile(f);
              e.target.value = "";
            }}
          />
        </label>

        <div className="flex justify-between mt-1 text-sm text-gray-500">
          <span>Format: First Name, Last Name, Email, Role</span>
          <button
            type="button"
            className="text-[#004f73] hover:underline"
            onClick={() => {
              const blob = new Blob([csvSample], { type: "text/csv" });
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = "Sample-Data.csv";
              a.click();
            }}
          >
            Sample Data
          </button>
        </div>

        {invalidRows.length > 0 ? (
          <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            {invalidRows.length} row(s) need a valid email and role before you can invite.
          </p>
        ) : null}

        {duplicateConfirm ? (
          <p className="mt-3 text-sm text-[#004f73] bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <span className="font-medium">{duplicateConfirm.email}</span>{" "}
            {duplicateConfirm.confirmationReason === "same_company"
              ? `is already invited to ${companyName} — confirm in the dialog, or upload a different file.`
              : "is already on another company — confirm in the dialog, or upload a different file."}
          </p>
        ) : null}

        {extractedRows.length > 0 && (
          <div className="mt-4 max-h-40 overflow-auto border rounded text-xs">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left">Email</th>
                  <th className="p-2 text-left">Role</th>
                </tr>
              </thead>
              <tbody>
                {extractedRows.map((r) => {
                  const rowInvalid = !r.email?.trim() || !r.designation?.trim();
                  return (
                    <tr
                      key={r._uid}
                      className={`border-t ${rowInvalid ? "bg-amber-50" : ""}`}
                    >
                      <td className="p-2">
                        {r.first_name} {r.last_name}
                      </td>
                      <td className="p-2 text-[#004f73] break-all">{r.email || "—"}</td>
                      <td className="p-2 min-w-[140px]">
                        {r.rawRole && !r.designation ? (
                          <p className="text-[10px] text-amber-700 mb-1 truncate" title={r.rawRole}>
                            Unrecognized: {r.rawRole}
                          </p>
                        ) : null}
                        <InviteRoleField
                          roleOptions={roles}
                          value={r.designation}
                          onChange={(v) => {
                            setExtractedRows((rows) =>
                              rows.map((x) =>
                                x._uid === r._uid ? { ...x, designation: v } : x
                              )
                            );
                          }}
                          showHint={false}
                          selectClassName={`border rounded px-1 py-0.5 w-full text-xs ${
                            !r.designation ? "border-amber-400" : ""
                          }`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-4">
          <button type="button" className="grey_btn text-sm" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="blue_btn text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={busy || !canInvite}
            title={
              !canInvite ? "Add a valid file with email and role for every row" : ""
            }
            onClick={() => submitBulkInvite(false)}
          >
            {busy ? <Spinner className="h-4 w-4" /> : null}
            Invite
          </button>
        </div>
      </div>

      <DuplicateEmailConfirmDialog
        open={!!duplicateConfirm}
        email={duplicateConfirm?.email}
        otherCompanies={duplicateConfirm?.otherCompanies}
        username={duplicateConfirm?.username}
        targetCompanyName={companyName}
        confirmationReason={duplicateConfirm?.confirmationReason}
        busy={busy}
        onConfirm={async () => {
          setDuplicateConfirm(null);
          await submitBulkInvite(true);
        }}
        onCancel={() => {
          setDuplicateConfirm(null);
          setBusy(false);
        }}
      />
    </div>
  );

  return createPortal(modal, document.body);
}
